import { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useAppVersion } from '../lib/appVersion.js';

/**
 * Prompts the user to reload when the server is serving a newer build.
 *
 * Two independent signals feed this, because neither alone is reliable:
 *
 *  - the service worker's `needRefresh`, which fires when it sees a new
 *    registration, and
 *  - a direct comparison of build identities, which catches the case where an
 *    installed PWA has been open long enough that the worker never re-checked.
 *
 * The worker is registered in 'prompt' mode rather than 'autoUpdate' so it
 * cannot swap itself in mid-session, which can leave a loaded page requesting
 * chunks that no longer exist. Reloading is a deliberate act.
 */
export function UpdatePrompt(): JSX.Element | null {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      // A long-lived PWA session would otherwise only notice a new build on a
      // cold start. Check hourly so an update surfaces the same day.
      if (registration) {
        setInterval(() => void registration.update(), 60 * 60 * 1000);
      }
    },
  });

  const { outdated, reload } = useAppVersion();

  // Dismissal is per-session only. The build comparison keeps reporting the
  // mismatch until the page actually reloads, so without this the banner could
  // never be put away — but it should still come back on the next visit.
  const [dismissed, setDismissed] = useState(false);

  const updateAvailable = (needRefresh || outdated) && !dismissed;

  if (!offlineReady && !updateAvailable) return null;

  const applyUpdate = async (): Promise<void> => {
    // Prefer the worker's own path when it knows about the update; fall back to
    // a cache-clearing reload when only the build comparison spotted it.
    if (needRefresh) {
      await updateServiceWorker(true);
      return;
    }
    await reload();
  };

  const dismiss = (): void => {
    setOfflineReady(false);
    setNeedRefresh(false);
    setDismissed(true);
  };

  return (
    <div className="update-toast" role="status" aria-live="polite">
      {updateAvailable ? (
        <>
          <div className="grow">
            <strong>Neue Version verfügbar</strong>
            <div className="hint">Zum Aktualisieren neu laden.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => void applyUpdate()}>
            Neu laden
          </button>
          <button className="btn btn-ghost btn-sm" onClick={dismiss}>
            Später
          </button>
        </>
      ) : (
        <>
          <div className="grow">
            <strong>Offline bereit</strong>
            <div className="hint">Die App funktioniert jetzt auch ohne Verbindung.</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={dismiss}>
            OK
          </button>
        </>
      )}
    </div>
  );
}
